<template>
  <div class="menu-manage">
    <el-card shadow="never" class="table-card">
      <template #header>
        <div class="card-header">
          <div class="category-tabs">
            <el-button
              v-for="category in menuStore.categoryAll"
              :key="category.categoryId"
              :type="activeCategory === category.categoryId ? 'primary' : 'default'"
              size="small"
              @click="filterByCategory(category.categoryId)"
            >
              {{ category.name }}
            </el-button>
          </div>
          <el-button type="primary" size="default" @click="addDish">新增菜品</el-button>
        </div>
      </template>

      <!-- 菜品卡片区域 -->
      <div class="dish-grid">
        <el-row :gutter="10">
          <el-col
            v-for="dish in filteredDishes"
            :key="dish.dishId"
            :xs="24"
            :sm="12"
            :md="8"
            :lg="3"
          >
            <el-card class="dish-card" shadow="hover" :body-style="{ padding: '0' }">
              <div class="dish-image">
                <DishImage :fileId="dish.fileId" :alt="dish.name"></DishImage>
                <div class="dish-tags" v-if="dish.tag">
                  <span class="dish-tag" :class="menuStore.getTagClass(dish.tagName)">
                    {{ dish.tagName }}
                  </span>
                </div>
              </div>
              <div class="dish-info">
                <div class="dish-meta">
                  <h3 class="dish-name" :title="dish.name">{{ dish.name }}</h3>
                  <el-tag size="small" type="info" class="dish-category-tag">
                    {{ dish.categoryName }}
                  </el-tag>
                </div>
                <div class="price-section">
                  <span class="current-price">¥{{ dish.currentPrice.toFixed(1)}}</span>
                  <span class="original-price" v-if="dish.currentPrice !== dish.price">
                    ¥{{ dish.price.toFixed(1) }}
                  </span>
                </div>
                <div class="dish-footer">
                  <div class="footer-left">
                    <el-tooltip content="上架/下架" placement="top">
                      <el-switch
                        :model-value = "dish.displayFlag"
                        @change="(val) => dishDisplayChange(dish, val)"
                        size="small"
                      />
                    </el-tooltip>
                  </div>
                  <div class="footer-right">
                    <el-button type="primary" link @click="editDish(dish)">编辑</el-button>
                    <el-button type="danger" link @click="deleteDish(dish)">删除</el-button>
                  </div>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </div>
    </el-card>

    <!-- 新增/编辑菜品对话框 -->
    <el-dialog
      :title="dialogTitle"
      v-model="dialogVisible"
      width="500px"
      destroy-on-close
      @close="resetForm"
    >
      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        label-width="80px"
        label-position="right"
      >
        <!-- 菜品名称 -->
        <el-form-item label="菜品名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入菜品名称" />
        </el-form-item>

        <!-- 菜品价格 + 折扣开关 -->
        <el-form-item label="菜品价格" prop="price" required>
          <div style="display: flex; align-items: center; gap: 12px; flex-wrap: wrap;">
            <el-input
              v-model.number="form.price"
              type="number"
              step="0.5"
              min="0"
              placeholder="请输入价格"
              style="width: 150px"
            />
            <el-checkbox v-model="form.hasDiscount" @change="setDiscount">
              设置折扣
            </el-checkbox>
          </div>
        </el-form-item>

        <!-- 折扣设置区域（条件显示） -->
        <el-form-item label="折扣信息" v-if="form.hasDiscount">
          <div style="display: flex; gap: 16px; align-items: center; flex-wrap: wrap;">
            <el-input
              v-model.number="form.rate"
              type="number"
              step="0.05"
              min="0"
              max="1"
              placeholder="折扣率 (0.1~1)"
              style="width: 140px"
              @input="calcDiscountedPrice"
            />
            <span>折后价：¥{{ discountedPrice.toFixed(1) }}</span>
          </div>
          <div style="margin-top: 12px;">
            <el-date-picker
              v-model="form.discountDateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="YYYY-MM-DD"
              style="width: 80%"
            />
          </div>
        </el-form-item>

        <!-- 菜品分类 -->
        <el-form-item label="菜品分类" prop="categoryId">
          <el-select v-model="form.categoryId" placeholder="请选择分类" style="width: 100%">
            <el-option
              v-for="cat in menuStore.categorys"
              :key="cat.categoryId"
              :label="cat.name"
              :value="cat.categoryId"
            />
          </el-select>
        </el-form-item>

        <!-- 菜品标签 -->
        <el-form-item label="标签" prop="tags">
          <div class="tag-group">
            <el-tag
              v-for="tag in menuStore.tags"
              :key="tag.categoryId"
              :type="form.tag === tag.categoryId ? 'success' : 'info'"
              @click="tagClick(tag.categoryId)"
            >
              {{ tag.name }}
            </el-tag>
          </div>
        </el-form-item>

        <!-- 图片上传（支持本地文件） -->
        <el-form-item label="菜品图片" prop="image">
          <el-upload
            ref="uploadRef"
            class="avatar-uploader"
            action="#"
            :auto-upload="false"
            :show-file-list="false"
            :on-change="handleImageChange"
            accept="image/*"
          >
            <img v-if="form.image" :src="form.image" class="avatar-preview" />
            <DishImage v-else-if="form.fileId" :fileId="form.fileId"></DishImage>
            <el-icon v-else class="avatar-uploader-icon"><Plus /></el-icon>
          </el-upload>
          <div class="el-upload__tip" style="margin-top: 4px;">点击选择图片，支持 jpg/png 格式</div>
        </el-form-item>

        <!-- 菜品描述 -->
        <el-form-item label="菜品描述" prop="description">
          <el-input
            v-model="form.description"
            type="textarea"
            :rows="3"
            placeholder="请输入菜品描述"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitForm">
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useMenuStore } from '@/stores/common/menuStore'
import DishImage from '@/components/DishImage.vue'

const menuStore = useMenuStore()
const activeCategory = ref('all') // 当前选中的分类ID

const emptyDish = {
  dishId: '',
  name: '',
  price: 0,
  categoryId: '',
  fileId: null,
  image: null,
  description: '',
  hasDiscount: false,      // 是否启用折扣
  rate: 1.0,        // 折扣率，默认无折扣
  discountDateRange: [],
  tag: null
}

// 对话框相关
const dialogVisible = ref(false)
const dialogType = ref('add') // 'add' 或 'edit'
const formRef = ref()
const form = ref(emptyDish)

// 计算折后价
const discountedPrice = computed(() => {
  return form.value.price * form.value.rate
})

// 处理折扣开关变化
const setDiscount = () => {
  form.value.rate = 1.0  // 关闭折扣时重置
  form.value.discountDateRange = []
}

// 当折扣率变化时，可自动触发计算（已在模板中绑定 calcDiscountedPrice，但该函数可不做额外操作）
const calcDiscountedPrice = () => {
  // 可留空或加入边界校验
  if (form.value.rate < 0) form.value.rate = 0
  if (form.value.rate > 1) form.value.rate = 1
}

// 处理图片上传
const handleImageChange = (file) => {
  const reader = new FileReader()
  reader.readAsDataURL(file.raw)
  reader.onload = () => {
    form.value.image = reader.result  // 保存 base64 到 imgUrl
  }
}

// 表单验证规则
const rules = {
  name: [{ required: true, message: '请输入菜品名称', trigger: 'blur' }],
  price: [{ required: true, message: '请输入价格', trigger: 'blur' }],
  categoryId: [{ required: true, message: '请选择分类', trigger: 'change' }]
}

// 对话框标题
const dialogTitle = computed(() => (dialogType.value === 'add' ? '新增菜品' : '编辑菜品'))

// 根据分类过滤菜品
const filteredDishes = computed(() => {
  if (activeCategory.value === 'all') return menuStore.dishList
  return menuStore.dishList.filter(dish => dish.categoryId === activeCategory.value || dish.tag === activeCategory.value)
})

// 分类点击
const filterByCategory = (categoryId) => {
  activeCategory.value = categoryId
}

// tag选择
const tagClick = (categoryId) =>{
  form.value.tag = form.value.tag === categoryId ? null : categoryId;
}

// 新增
const addDish = () => {
  dialogType.value = 'add'
  dialogVisible.value = true
}

// 编辑
const editDish = (dish) => {
  form.value = { ...dish }
  dialogType.value = 'edit'
  dialogVisible.value = true
}

const dishDisplayChange = async (dish, newVal) => {
  ElMessageBox.confirm(`确认菜品"${dish.name}"要${newVal ? '上架' : '下架'}吗？`, '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    await menuStore.displayChange({dishId : dish.dishId, displayFlag : newVal})
    ElMessage.success('菜品信息已更新')
  }).catch(() => {})
}

// 重置表单
const resetForm = () => {
  form.value = { ...emptyDish }
  formRef.value?.resetFields()
}

// 提交表单
const submitForm = async () => {
  if (!formRef.value) return
  await formRef.value.validate(async (valid) => {
    if (!valid) return
    const p = {...form.value }
    if(p.discountDateRange && p.discountDateRange.length == 2){
      p.startDate = p.discountDateRange[0]
      p.endDate = p.discountDateRange[1]
    }
    await menuStore.editDish(p)
    ElMessage.success('菜品信息已更新')
    dialogVisible.value = false
  })
}

// 删除
const deleteDish = (dish) => {
  ElMessageBox.confirm(`确认删除菜品"${dish.name}"吗？`, '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    await menuStore.deleteDish({dishId : dish.dishId, fileId: dish.fileId})
    ElMessage.success('菜品已删除')
  }).catch(() => {})
}

// 加载数据
onMounted(async () => {
  await menuStore.getCategoryList()
  await menuStore.getDishList()
})
</script>

<style scoped>
.menu-manage {
  width: 100%;
}
.table-card {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.category-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.category-tabs .el-button {
  margin-left: 0;
}
.dish-grid {
  margin-top: 20px;
}
.dish-card {
  border-radius: 6px;
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
  margin-bottom: 16px;
}
.dish-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}
.dish-image {
  position: relative;
  height: 150px;
  overflow: hidden;
  background-color: #f5f5f5;
}

.dish-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.dish-card:hover .dish-image img {
  transform: scale(1.05);
}
.dish-info {
  padding: 16px;
}
.dish-name {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dish-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
}
.price-section {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.current-price {
  font-size: 18px;
  color: #ff6b6b;
  font-weight: bold;
}

.original-price {
  font-size: 13px;
  color: #999;
  text-decoration: line-through;
}

.dish-category-tag {
  font-size: 12px;
}
.preview-image {
  display: flex;
  justify-content: center;
}
.avatar-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.avatar-uploader:hover {
  border-color: #409eff;
}
.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 120px;
  height: 120px;
  text-align: center;
  line-height: 120px;
}
.avatar-preview {
  width: 120px;
  height: 120px;
  object-fit: contain;
}

.tag-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px; /* 标签之间的间距 */
}
.tag-group .el-tag {
  cursor: pointer; /* 鼠标变为手型，表示可点击 */
  transition: all 0.2s ease; /* 平滑过渡效果 */
  border-radius: 10px; /* 更圆润的圆角 */
  padding: 0 10px;
  height: 24px;
  line-height: 30px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid transparent; /* 预留边框位置，避免选中时抖动 */
}
.tag-group .el-tag:hover {
  transform: translateY(-1px); /* 悬停轻微上浮 */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); /* 添加阴影 */
}
/* 选中状态（success类型）自定义颜色 */
.tag-group .el-tag.el-tag--success {
  background-color: #f0f9eb;
  border-color: #67c23a;
  color: #67c23a;
}
/* 未选中状态（info类型）自定义颜色 */
.tag-group .el-tag.el-tag--info {
  background-color: #f4f4f5;
  border-color: #e9e9eb;
  color: #909399;
}
.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px; /* 标签之间的间距 */
}

.dish-tags {
  position: absolute;
  top: 0px;
  left: 0px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dish-tag {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  color: white;
  white-space: nowrap;
}

.tag-signature {
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%);
}
.tag-hot {
  background: linear-gradient(135deg, #ff8e53 0%, #ffb86b 100%);
}
.tag-discount {
  background: linear-gradient(135deg, #4cd964 0%, #5ac8fa 100%);
}
.tag-spicy {
  background: linear-gradient(135deg, #ff2d55 0%, #ff6b6b 100%);
}

.dish-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}
.footer-left,
.footer-right {
  display: flex;
  gap: 2px;
}
</style>